import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { BaseComponent } from '../../../components/base/base.component';
import { TextResourceService } from '../../../services/text-resource.service';
import { Answer } from '../../models/answer';
import { Question, QuestionType } from '../../models/question';
import { KeyValue } from '@angular/common';
import { ErrorMessage, FormOption, NgfFormDirective, FormSelectComponent, FormRadioComponent } from '@cibg/ui-components';
import { AdvieswijzerService } from '../../advieswijzer.service';

@Component({
    selector: 'app-question',
    templateUrl: './question.component.html',
    imports: [ReactiveFormsModule, NgfFormDirective, FormSelectComponent, FormRadioComponent]
})
export class QuestionComponent extends BaseComponent implements OnInit {
    questionTypes = QuestionType;

    @Input() question: Question;
    @Input() preFilledAnswer: Answer;

    questionForm: UntypedFormGroup;
    dropdownOptions: Array<KeyValue<string, string>> = [];
    radioOptions: Array<FormOption> = [];
    validationMessages: Array<ErrorMessage> = [];

    get hasPreviousQuestion() {
        return this.question.id !== 'professionalGroup';
    }

    get answer(): Answer {
        const value = this.questionForm.controls[this.question.id].value;
        const key = this.question.options.find((opt) => opt.value != null && opt.value.toString() === value.toString()).key;
        const text = this.question.type === QuestionType.DropDown
            ? this.capitalize(key)
            : this.getResource(key);

        const answ = new Answer();
        answ.question = this.question.id;
        answ.questionText = this.getResource(this.question.text);
        answ.answer = value;
        answ.answerText = text;

        return answ;
    }

    constructor(
        textResourceService: TextResourceService,
        private readonly advieswijzerService: AdvieswijzerService) {
        super(textResourceService, 'QuestionComponent');
    }

    ngOnInit() {
        this.questionForm = new UntypedFormGroup({
            [this.question.id]: new UntypedFormControl('', [Validators.required])
        });

        this.validationMessages = [{
            validation: 'required',
            message: this.supplant(this.resources.interpolatedRequiredFieldLabel, [this.getResource(`${this.question.id}Text`)])
        }];

        switch (this.question.type) {
            case QuestionType.DropDown:
                this.dropdownOptions = this.question.options.map((opt) => {
                    return {
                        key: opt.value,
                        value: this.capitalize(opt.key)
                    };
                });

                this.dropdownOptions.unshift({
                    key: '',
                    value: this.resources.emptySelectOptionText
                });

                break;
            case QuestionType.Radio:
                this.radioOptions = this.question.options.map((opt, i) => {
                    return {
                        id: `radio-answer-${i}`,
                        value: opt.value,
                        label: this.getResource(opt.key)
                    };
                });
                break;
        }

        this.fillAnswer();
    }

    goToNextQuestion() {
        if (this.questionForm.valid) {
            this.advieswijzerService.next(this.answer);
        }
    }

    goToPreviousQuestion() {
        this.advieswijzerService.previous();
    }

    private fillAnswer() {
        if (this.preFilledAnswer) {
            // timeout to allow options to be filled from ngFor
            setTimeout(() => {
                this.questionForm.controls[this.question.id].setValue(this.preFilledAnswer.answer);
            });
        }
    }

    private supplant(source: string, o: { [x: string]: any; }) {
        const supplantFunc = (a: any, b: string | number) => {
            const r = o[b];
            return typeof r === 'string' || typeof r === 'number' ? r : a;
        };

        return source.replace(/{([^{}]*)}/g, supplantFunc);
    }
}
